import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { StarIcon, ShoppingCartIcon, HeadphonesIcon, BookOpenIcon, BookIcon, HeartIcon, FlameIcon, MoonIcon, UsersIcon, BellIcon, GiftIcon } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { usePlatform } from '../../context/PlatformContext';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { spiceLevelMap, darknessLevelMap } from './constants';
import TagsLine from './TagsLine';
// Mock seller donation data (would come from book/author data in real app)
const sellerDonationInfo = {
  hasDonation: true,
  percentage: 5,
  nonprofitName: "Children's Literacy Foundation",
  nonprofitSlug: 'childrens-literacy-foundation'
};
interface BookInfoProps {
  book: any;
  selectedFormat: any;
  setSelectedFormat: (format: any) => void;
  setCartItemCount: (count: number) => void;
  hideDonationCallout?: boolean;
}
export const BookInfo = ({
  book,
  selectedFormat,
  setSelectedFormat,
  setCartItemCount,
  hideDonationCallout = false
}: BookInfoProps) => {
  const {
    darkMode
  } = useDarkMode();
  const {
    isAfterTide
  } = usePlatform();
  const rt = useRoleTheme();
  const authorSlug = book.authorSlug || book.author.toLowerCase().replace(/\s+/g, '-');
  const authorLink = isAfterTide ? `/aftertide/author/${authorSlug}` : `/author/${authorSlug}`;
  const reviewsLink = isAfterTide ? `/aftertide/book/${book.id}/reviews` : `/book/${book.id}/reviews`;
  return <div className="md:col-span-2 space-y-4" data-id="element-7950" data-mp="fju0uly05syy">
      {/* Title & Series */}
      <div data-id="element-7951" data-mp="akqsaritd4m8">
        <h1 className={`text-2xl sm:text-3xl font-bold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7952" data-mp="akhbnujns9t7">
          {book.title}
        </h1>
        {book.series && <div className={`text-lg font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-7953" data-mp="2vay8ugoy7wu8">
            {book.series.name} - Book {book.series.position}
            {book.series.books && <span className={`text-base ml-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`} data-id="element-7954" data-mp="2rudejcvr21al">
                of {book.series.books.length}
              </span>}
          </div>}
        <p className={`text-xl mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-7955" data-mp="2iscd03thgt34">
          by{' '}
          <Link to={authorLink} className={`transition-colors ${darkMode ? 'text-indigo-400 hover:text-indigo-300' : 'hover:text-indigo-500'}`} data-id="element-7956" data-mp="292sytbgptnel">
            {book.author}
          </Link>
          {book.formats && book.formats.some((f: any) => f.type === 'Audiobook' && f.narrator) && <>
                {', '}
                <span className="text-gray-500 dark:text-gray-400" data-id="element-7957" data-mp="1q48b7te1905p">Narrated by</span>{' '}
                {book.formats.filter((f: any) => f.type === 'Audiobook' && f.narrator).map((f: any, idx: number) => <Link key={idx} to={isAfterTide ? `/aftertide/narrator/${f.narrator!.toLowerCase().replace(/\s+/g, '-')}` : `/narrator/${f.narrator!.toLowerCase().replace(/\s+/g, '-')}`} className={`transition-colors ${darkMode ? 'text-indigo-400 hover:text-indigo-300' : 'hover:text-indigo-500'}`} data-id="element-7958" data-mp="1bhrm9csflg7c" data-mpi={idx}>
                      {f.narrator}
                    </Link>)}
              </>}
        </p>
      </div>

      {/* Rating and Heat Level */}
      <div className="flex flex-wrap items-center gap-3 mb-3" data-id="element-7959" data-mp="1z6amr658ooov">
        {!book.coming_soon && <div className="flex items-center" data-id="element-7960" data-mp="2d63lnoqx6b7x">
            <div className="flex text-amber-500" data-id="element-7961" data-mp="2d6v56178sihu">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className={`h-5 w-5 ${i < Math.floor(book.rating) ? 'fill-current' : 'stroke-current fill-transparent'}`} data-id="element-7962" data-mp="296uqqgk587as" data-mpi={i} />)}
            </div>
            <Link to={reviewsLink} className={`text-sm ml-2 hover:underline ${darkMode ? 'text-gray-300 hover:text-indigo-400' : 'text-gray-700 hover:text-indigo-500'}`} data-id="element-7963" data-mp="bf35jjx81h3l">
              {book.rating} ({book.reviewCount} reviews)
            </Link>
          </div>}
        {book.coming_soon && <span className={`inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} ${isAfterTide ? 'border border-gray-500/30' : ''} text-xs font-medium ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'}`} data-id="element-7964" data-mp="3op0p46xp6ggg">
            Not yet rated
          </span>}
        {/* Spice Level */}
        {(() => {
        const level = book.spiceLevel || 0;
        const spice = spiceLevelMap[level];
        if (!spice) return null;
        return <Link to={`/spice-darkness`} className={`inline-flex items-center gap-1.5 px-3 py-1 ${rt.badgeRadius} ${isAfterTide ? 'border border-rose-500/30' : ''} text-sm font-medium shadow-sm transition-opacity hover:opacity-80 ${darkMode ? `${spice.darkBg} ${spice.darkText}` : `${spice.bg} ${spice.text}`}`} data-id="element-7965" data-mp="2xfedt532iijp">
              <FlameIcon className={`h-3.5 w-3.5 ${spice.iconColor}`} fill="currentColor" data-id="element-7966" data-mp="2epyaka000bzl" />
              {spice.label}
            </Link>;
      })()}
        {/* Darkness Level */}
        {(() => {
        const level = book.darknessLevel || 0;
        const darkness = darknessLevelMap[level];
        if (!darkness) return null;
        return <Link to={`/spice-darkness`} className={`inline-flex items-center gap-1.5 px-3 py-1 ${rt.badgeRadius} ${isAfterTide ? 'border border-purple-500/30' : ''} text-sm font-medium shadow-sm transition-opacity hover:opacity-80 ${darkMode ? `${darkness.darkBg} ${darkness.darkText}` : `${darkness.bg} ${darkness.text}`}`} data-id="element-7967" data-mp="3xmzox417mib">
              <MoonIcon className={`h-3.5 w-3.5 ${darkness.iconColor}`} fill="currentColor" data-id="element-7968" data-mp="mw9sgv3jor8a" />
              {darkness.label}
            </Link>;
      })()}
      </div>

      {/* Tags Line */}
      <TagsLine book={book} data-id="element-7969" data-mp="11r65rmq7o4s8" />

      {/* Donation Callout */}
      {sellerDonationInfo.hasDonation && !hideDonationCallout && <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${darkMode ? 'bg-rose-900/20 border border-rose-800/30' : 'bg-rose-50 border border-rose-100'}`} data-id="element-7970" data-mp="15shor9ekxrrg">
          <GiftIcon className={`h-4 w-4 flex-shrink-0 ${darkMode ? 'text-rose-400' : 'text-rose-500'}`} data-id="element-7971" data-mp="wr1mxq1tyxyu" />
          <p className={`text-sm ${darkMode ? 'text-rose-300' : 'text-rose-700'}`} data-id="element-7972" data-mp="3clk3xbvy49aj">
            <span className="font-medium" data-id="element-7973" data-mp="3hg7jumcvvvdi">
              {sellerDonationInfo.percentage}% of this sale
            </span>{' '}
            goes to{' '}
            <Link to={`/donations/nonprofit/${sellerDonationInfo.nonprofitSlug}`} className={`font-medium underline underline-offset-2 ${darkMode ? 'hover:text-rose-200' : 'hover:text-rose-800'}`} data-id="element-7974" data-mp="34ywsycmlo5f3">
              {sellerDonationInfo.nonprofitName}
            </Link>{' '}
            ❤️
          </p>
        </div>}

      {/* Why Choose Information Box */}
      {book.characterRelationships && book.characterRelationships.some((rel: any) => rel.style?.some((style: string) => style.toLowerCase().includes('why choose'))) && <div className={`border rounded-lg p-4 shadow-sm ${darkMode ? 'bg-pink-900/10 border-pink-800' : 'bg-pink-50 border-pink-100'}`} data-id="element-7975" data-mp="2v8u2aj2yb3jx">
            <h3 className={`text-base font-semibold mb-2 flex items-center ${darkMode ? 'text-pink-300' : 'text-pink-800'}`} data-id="element-7976" data-mp="22m96n6fg05ak">
              <UsersIcon className="h-5 w-5 mr-1" data-id="element-7977" data-mp="1i20jfg54zuot" /> Why Choose Romance
            </h3>
            <p className={`text-sm mb-2 ${darkMode ? 'text-pink-200' : 'text-pink-700'}`} data-id="element-7978" data-mp="1m3bzaty7l1jt">
              This is a "Why Choose" romance where the main character maintains
              loving relationships with multiple partners instead of selecting
              just one.
            </p>
            <Link to="/relationship-types#why-choose" className={`text-sm font-medium inline-block ${darkMode ? 'text-pink-300 hover:text-pink-200' : 'text-pink-800 hover:text-pink-900'}`} data-id="element-7979" data-mp="2503qdpjzskyf">
              Learn more about Why Choose romance →
            </Link>
          </div>}

      {/* Book Description */}
      <div className={`rounded-lg p-4 shadow-sm ${darkMode ? 'bg-gray-800' : 'bg-white'}`} data-id="element-7980" data-mp="2jqawd5stcbi8">
        <p className={`whitespace-pre-line ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-7981" data-mp="2oom6lhn0so0z">
          {book.description ? book.description : 'No description available for this book.'}
        </p>
        {book.description && book.description.length > 500 && <button className={`text-sm font-medium mt-2 transition-colors ${darkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-500 hover:text-indigo-700'}`} data-id="element-7982" data-mp="32thd9843v88">
            Read more
          </button>}
      </div>

      {/* Format Selection */}
      <div data-id="element-7983" data-mp="qsnvrv82xrul">
        <h3 className={`text-base sm:text-lg font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-7984" data-mp="qsxjfc5njmx4">
          {book.coming_soon ? 'Pre-Order Formats' : 'Available Formats'}
        </h3>
        <div className="flex flex-wrap gap-2 sm:gap-3" data-id="element-7985" data-mp="h0i30ja076ec">
          {(book.formats || [selectedFormat]).map((format: any, index: number) => <button key={index} onClick={() => setSelectedFormat(format)} disabled={!format.available} className={`px-4 py-2 rounded-lg border flex items-center shadow-sm transition-all duration-200 ${selectedFormat.type === format.type ? book.coming_soon ? 'border-amber-500 bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' : 'border-indigo-500 bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300' : !format.available ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed dark:border-gray-700 dark:bg-gray-800 dark:text-gray-500' : `border-gray-300 bg-white text-gray-700 hover:bg-gray-50 ${darkMode ? 'dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700' : ''}`}`} data-id="element-7986" data-mp="19q88y51hf1zd" data-mpi={index}>
                {format.type === 'Audiobook' ? <HeadphonesIcon className="h-4 w-4 mr-2" data-id="element-7987" data-mp="3f66yk6m5ddk1" /> : format.type === 'eBook' ? <BookOpenIcon className="h-4 w-4 mr-2" data-id="element-7988" data-mp="3a8yeg0hfuzg5" /> : <BookIcon className="h-4 w-4 mr-2" data-id="element-7989" data-mp="3tt5ofx5cvzwn" />}
                {format.type} -{' '}
                {format.available ? `$${format.price.toFixed(2)}` : 'TBA'}
              </button>)}
        </div>
        {/* Add to Cart & Wishlist Buttons */}
        <div className="mt-4 flex flex-col sm:flex-row gap-3" data-id="element-7990" data-mp="3ouc87wyopuss">
          <button onClick={() => {
          const cartItem = {
            id: book.id,
            title: book.title,
            format: selectedFormat.type,
            price: selectedFormat.price,
            quantity: 1
          };
          setCartItemCount((prev: any) => prev + 1);
        }} className={`text-white font-medium px-6 py-3 rounded-lg flex items-center justify-center shadow-sm transition-all duration-200 ${book.coming_soon ? 'bg-amber-500 hover:bg-amber-600' : 'bg-indigo-500 hover:bg-indigo-600'}`} data-id="element-7991" data-mp="3owoyqyiiddhg">
            <ShoppingCartIcon className="h-5 w-5 mr-2" data-id="element-7992" data-mp="2w818eoiwjxx1" />
            {book.coming_soon ? 'Pre-Order Now' : 'Add to Cart'}
          </button>
          <button className={`border font-medium px-6 py-3 rounded-lg flex items-center justify-center shadow-sm transition-all duration-200 ${book.coming_soon ? 'border-amber-300 text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/20' : `border-gray-300 text-gray-700 hover:bg-gray-50 ${darkMode ? 'dark:border-gray-600 dark:text-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700' : 'bg-white'}`}`} data-id="element-7993" data-mp="28a1wogvn269r">
            {book.coming_soon ? <>
                <BellIcon className="h-5 w-5 mr-2" data-id="element-7994" data-mp="2m1gptgj9fnuj" />
                Notify Me on Release
              </> : <>
                <HeartIcon className="h-5 w-5 mr-2" data-id="element-7995" data-mp="2h2nd3qbss0vf" />
                Add to Wishlist
              </>}
          </button>
        </div>
      </div>
    </div>;
};
export default BookInfo;